export const resultsGuide = {
  categories: {
    minimal: {
      range: { min: 0, max: 13 },
      percentage: { min: 0, max: 24 },
      title: "MINIMAL BRIDGE FRAMEWORK NEED",
      color: "green",
      subtitle: "You have exceptional self-management skills",
      description: "You consistently follow through, have effective systems, and maintain momentum. You might only need coaching for extremely challenging goals or specialized areas.",
      timeline: "As-needed consultation",
      immediateActions: [
        "Consider mentoring others with your strong systems",
        "Focus on stretch goals or leadership development",
        "Apply your skills to help others in your network"
      ],
      advancedApplications: [
        "Leadership coaching for managing others",
        "Optimization for stretch goals", 
        "Specialized expertise in complex areas",
        "Teaching/mentoring others"
      ],
      expectedResults: [
        "Continued excellence in goal achievement",
        "Enhanced leadership capabilities",
        "Expanded impact through mentoring others",
        "Mastery of advanced optimization techniques"
      ]
    },
    selective: {
      range: { min: 14, max: 27 },
      percentage: { min: 25, max: 50 },
      title: "SELECTIVE BRIDGE FRAMEWORK APPLICATION",
      color: "blue",
      subtitle: "Specific phases could enhance your strong foundation",
      description: "You generally follow through on commitments and have effective systems. You might benefit from specific advanced techniques or during major transitions.",
      timeline: "4-8 weeks per targeted area",
      approach: "Choose 1-2 Specific Areas",
      immediateActions: [
        "Identify your lowest-scoring section for focused improvement",
        "Choose one specific area to optimize over next 4-8 weeks",
        "Consider targeted coaching sessions for specific challenges"
      ],
      expectedResults: [
        "Enhanced performance in targeted areas",
        "Refined systems and processes",
        "Increased confidence in weaker areas",
        "Maintained strength in existing systems"
      ]
    },
    moderate: {
      range: { min: 28, max: 40 },
      percentage: { min: 51, max: 74 },
      title: "MODERATE BRIDGE FRAMEWORK BENEFIT",
      color: "yellow",
      subtitle: "You would benefit significantly from systematic approach",
      description: "You have some systems in place but experience inconsistency. You're achieving below your potential and could accelerate progress with the right framework.",
      timeline: "8-12 weeks focusing on 2-3 phases",
      approach: "Focus on Your Highest Phase Scores",
      immediateActions: [
        "Start with your highest-scoring problem areas first",
        "Implement systematic approach across 2-3 key areas",
        "Consider group coaching or structured program"
      ],
      expectedResults: [
        "Significant improvement in consistency",
        "Better systems and accountability",
        "Increased momentum and motivation",
        "Measurable progress acceleration"
      ]
    },
    high: {
      range: { min: 41, max: 54 },
      percentage: { min: 75, max: 100 },
      title: "HIGH BRIDGE FRAMEWORK NEED",
      color: "red",
      subtitle: "You're an ideal candidate for the Bridge Framework™",
      description: "You're dealing with significant systematic challenges that require immediate, structured intervention. You likely feel frustrated with your current progress and are ready for a proven framework to break through these barriers.",
      timeline: "12-16 weeks",
      immediateActions: [
        "Schedule a consultation - You need systematic intervention now",
        "Begin environmental audit - Identify 3 biggest distractions", 
        "List all commitments - Prepare for elimination protocol"
      ],
      transformationTimeline: [
        { weeks: "1-2", phase: "Phase 1 (Clarity & Simplification)" },
        { weeks: "3", phase: "Phase 2 (72-Hour Momentum System)" },
        { weeks: "4-8", phase: "Phase 3 (Daily Consistency Building)" },
        { weeks: "9-16", phase: "Phases 4-6 (Advanced Systems)" }
      ],
      expectedResults: [
        "30% reduction in overwhelm within 2 weeks",
        "End start-stop cycles within 3 weeks", 
        "80%+ daily action completion by week 8",
        "Complete transformation in 12-16 weeks"
      ]
    }
  },

  phaseAnalysis: {
    foundation: {
      title: "Foundation Assessment",
      questions: [1, 2, 3, 4, 5, 6, 7, 8],
      maxScore: 24,
      levels: {
        strong: { min: 0, max: 5, description: "Excellent foundation for goal achievement" },
        minor: { min: 6, max: 11, description: "Generally solid foundation with occasional issues" },
        moderate: { min: 12, max: 17, description: "Some gaps in foundation that could be strengthened" },
        critical: { min: 18, max: 24, description: "Multiple fundamental challenges requiring immediate attention" }
      }
    },
    clarity: {
      title: "Phase 1: Clarity & Simplification", 
      questions: [9, 10, 11],
      maxScore: 9,
      levels: {
        low: { min: 0, max: 1, description: "Strong clarity and well-managed environment" },
        moderate: { min: 2, max: 3, description: "Minor adjustments to environment and commitments" },
        high: { min: 4, max: 6, description: "Some clarity and simplification needed for better focus" },
        urgent: { min: 7, max: 9, description: "Environmental chaos and commitment overwhelm are sabotaging your success" }
      }
    },
    action: {
      title: "Phase 2-3: Action & Execution",
      questions: [12, 13, 14], 
      maxScore: 9,
      levels: {
        low: { min: 0, max: 1, description: "Strong execution and momentum systems" },
        moderate: { min: 2, max: 3, description: "Occasional execution challenges" },
        high: { min: 4, max: 6, description: "Inconsistent action-taking and momentum issues" },
        urgent: { min: 7, max: 9, description: "Severe start-stop cycles and execution problems" }
      }
    },
    support: {
      title: "Phase 4: Support & Accountability",
      questions: [15, 16],
      maxScore: 6, 
      levels: {
        low: { min: 0, max: 0, description: "Strong openness to accountability and growth" },
        moderate: { min: 1, max: 2, description: "Moderate openness to accountability" },
        high: { min: 3, max: 4, description: "Some resistance to external support systems" },
        urgent: { min: 5, max: 6, description: "High resistance to accountability and growth challenges" }
      }
    },
    optimization: {
      title: "Phase 5-6: Optimization & Systems",
      questions: [17, 18],
      maxScore: 6,
      levels: {
        low: { min: 0, max: 0, description: "Advanced systems and data-driven approach" },
        moderate: { min: 1, max: 2, description: "Good systems with room for enhancement" },
        high: { min: 3, max: 4, description: "Some gaps in optimization and accountability" },
        urgent: { min: 5, max: 6, description: "Limited data usage and consequence systems" }
      }
    }
  },

  redFlags: [
    "Feeling completely overwhelmed by commitments",
    "Environment actively working against goals", 
    "Pattern of starting goals but never finishing",
    "Avoiding goal-setting due to past failures",
    "Procrastinating on important decisions for weeks/months",
    "No accountability systems whatsoever",
    "Living/working in constant chaos"
  ],

  nextStepsByCategory: {
    high: {
      title: "HIGH NEED - Immediate Actions",
      steps: [
        "This Week: Schedule Bridge Framework consultation",
        "Start immediately: Environmental decluttering",
        "Begin: 'One Less Thing Protocol'",
        "Timeline: Full framework implementation over 12-16 weeks"
      ]
    },
    moderate: {
      title: "MODERATE NEED - Strategic Actions", 
      steps: [
        "This Week: Identify your highest-scoring phase",
        "Start with: Most problematic area first",
        "Focus: 2-3 phases over 8-12 weeks",
        "Consider: Group coaching or targeted sessions"
      ]
    },
    selective: {
      title: "SELECTIVE APPLICATION - Targeted Actions",
      steps: [
        "This Week: Choose 1 specific improvement area",
        "Focus: Single phase implementation", 
        "Timeline: 4-8 weeks per area",
        "Approach: Self-directed with periodic check-ins"
      ]
    },
    minimal: {
      title: "MINIMAL NEED - Enhancement Actions",
      steps: [
        "As needed: Consultation for complex challenges",
        "Focus: Advanced optimization techniques",
        "Consider: Mentoring others or leadership development", 
        "Approach: Project-based or quarterly reviews"
      ]
    }
  },

  resources: {
    immediateDownloads: [
      { phase: "Phase 1", title: "'One Less Thing Protocol' Quick Start Guide" },
      { phase: "Phase 2", title: "'72-Hour Momentum System' Workbook" },
      { phase: "Phase 3", title: "'Minimum Viable Actions' Template" },
      { phase: "Phase 4", title: "'Accountability Triad Formation' Guide" }
    ],
    videoResources: [
      { title: "Environmental Success Audit", duration: "15 min" },
      { title: "Breaking the Start-Stop Cycle", duration: "20 min" },
      { title: "Building Accountability Systems", duration: "18 min" }
    ]
  }
};

export const getCategoryFromScore = (totalScore, maxScore) => {
  if (totalScore >= 41) return 'high';
  if (totalScore >= 28) return 'moderate'; 
  if (totalScore >= 14) return 'selective';
  return 'minimal';
};

export const getPhaseScore = (answers, phase) => {
  const phaseData = resultsGuide.phaseAnalysis[phase];
  if (!phaseData) return { score: 0, maxScore: 0, level: 'low' };

  const score = phaseData.questions.reduce((sum, questionIndex) => {
    const questionId = getQuestionId(questionIndex);
    return sum + (answers[questionId] || 0);
  }, 0);

  const level = getPhaseLevel(score, phaseData.levels);
  return {
    score,
    maxScore: phaseData.maxScore,
    level,
    description: phaseData.levels[level].description
  };
};

const getQuestionId = (questionIndex) => {
  // Map question indices to actual question IDs
  const questionMap = {
    1: 'foundation_1', 2: 'foundation_2', 3: 'foundation_3', 4: 'foundation_4',
    5: 'foundation_5', 6: 'foundation_6', 7: 'foundation_7', 8: 'foundation_8',
    9: 'clarity_1', 10: 'clarity_2', 11: 'clarity_3',
    12: 'action_1', 13: 'action_2', 14: 'action_3', 
    15: 'support_1', 16: 'support_2',
    17: 'optimization_1', 18: 'optimization_2'
  };
  return questionMap[questionIndex];
};

const getPhaseLevel = (score, levels) => {
  for (const [level, range] of Object.entries(levels)) {
    if (score >= range.min && score <= range.max) {
      return level;
    }
  }
  return 'low';
};